import { useCallback, useMemo, useState } from "react";
import { useInfiniteQuery, useQueries } from "@tanstack/react-query";
import { useApiClient, useModuleAvailability } from "@playbookai/api-clients";
import type { KpiCatalog } from "@playbookai/ui";
import { useAuthStore } from "../auth/auth-store.js";
import {
  getKpiCatalogItem,
  listKpiCatalogPage,
} from "../api/kpi-catalog-api.js";

/** Lazy, active-only KPI picker catalogue with cursor pagination and search. */
export function useKpiCatalog(): KpiCatalog {
  const config = useApiClient();
  const claims = useAuthStore((s) => s.claims);
  // KPI Manager off (ADR-0100): the Home KPI pickers read nothing.
  const kpiAvailable = useModuleAvailability().isAvailable("kpi");
  const authed = claims?.userId != null && claims?.tenantId != null;
  const canRead = authed && kpiAvailable;
  const [active, setActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<readonly string[]>([]);
  const activate = useCallback((ids: readonly string[]) => {
    setActive(true);
    setSelectedIds((previous) => {
      const next = [...new Set(ids)];
      return previous.length === next.length &&
        previous.every((id, index) => id === next[index])
        ? previous
        : next;
    });
  }, []);
  const query = useInfiniteQuery({
    queryKey: [
      "kpi-catalog",
      claims?.tenantId ?? "none",
      claims?.userId ?? "anon",
      searchQuery.trim(),
    ],
    enabled: active && canRead,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listKpiCatalogPage(config, {
        ...(searchQuery.trim() ? { q: searchQuery.trim() } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: 20,
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 60_000,
    retry: false,
  });
  const selectedQueries = useQueries({
    queries: selectedIds.map((id) => ({
      queryKey: ["kpi-catalog-item", claims?.tenantId ?? "none", id],
      queryFn: () => getKpiCatalogItem(config, id),
      enabled: active && canRead,
      staleTime: 60_000,
      retry: false,
    })),
  });
  // Not asking is not enough: KPIs cached from before KPI Manager went off
  // would still be offered, so nothing cached is either.
  const selectedItems = canRead
    ? selectedQueries.flatMap((item) => (item.data ? [item.data] : []))
    : [];
  const kpis = useMemo(() => {
    const rows = [
      ...selectedItems.map((item) => item.kpi),
      ...((canRead ? query.data?.pages : undefined)?.flatMap(
        (page) => page.items,
      ) ?? []),
    ];
    return [...new Map(rows.map((row) => [row.id, row])).values()];
  }, [canRead, query.data, selectedItems]);
  const selectionError = selectedQueries.some((item) => item.isError);
  return {
    kpis,
    archivedKpiIds: selectedItems
      .filter((item) => item.archived)
      .map((item) => item.kpi.id),
    isLoading:
      (query.isPending && query.isFetching) ||
      selectedQueries.some((item) => item.isPending && item.isFetching),
    isLoadingMore: query.isFetchingNextPage,
    error:
      query.error || selectionError ? "KPI choices couldn’t be loaded." : null,
    hasMore: canRead && query.hasNextPage,
    searchQuery,
    activate,
    onSearchChange: setSearchQuery,
    // `fetchNextPage` and `refetch` ignore `enabled`, so each asks itself.
    onLoadMore: () => {
      if (canRead && query.hasNextPage && !query.isFetchingNextPage)
        void query.fetchNextPage();
    },
    onRetry: () => {
      if (!canRead) return;
      void query.refetch();
      for (const selectedQuery of selectedQueries) {
        if (selectedQuery.isError) void selectedQuery.refetch();
      }
    },
  };
}
